import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import {IconButton, Switch, TextField} from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import React, {useState} from 'react';
import {
  Column,
  createColumn,
  ExpandableTable
} from '../../../components/ExpandableTable';
import {FeatureTemplate} from '../../../model/license/feature-template.model';
import {FormatUtils} from '../../../utils/format.utils';
import {CmsFeatureTemplateActionsBar} from './components/CmsFeatureTemplateActionsBar';
import {TranslationEditDrawer} from './components/TranslationEditDrawer';
import {useApiTranslations} from '../../../context/ApiTranslationsContext';
import {useCmsFeatureTemplatePageState} from './state/cms-feature-template-page-state';
import formatCurrency = FormatUtils.formatCurrency;
import formatDate = FormatUtils.formatDate;

export const CmsFeatureTemplatePage = () => {
  const state = useCmsFeatureTemplatePageState()
  const { refresh: refreshTranslations } = useApiTranslations()
  const [translationTarget, setTranslationTarget] = useState<{baseKey: string; type: 'module' | 'feature'} | null>(null);

  const buildDeliveryDateInput = (row: FeatureTemplate) => <Box display="flex" alignItems="center" gap={1}>
    <TextField
      type="date"
      value={row.deliveryDate}
      onChange={(e) => {
        state.onFeatureTemplateUpdated({...row, deliveryDate: e.target.value});
      }}
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={{ sx: { '& input': { padding: '4px 8px', height: '28px' } } }}
    />
  </Box>;

  const buildActivationCostInput = (row: FeatureTemplate) => <Box display="flex" alignItems="center" gap={1}>
    <TextField
      type="number"
      value={row.activationCost}
      onChange={(e) => {
        const val = e.target.value;
        state.onFeatureTemplateUpdated({...row, activationCost: val === '' ? row.activationCost : parseFloat(val)});
      }}
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={{ sx: { '& input': { padding: '4px 8px', height: '28px' } } }}
    />
  </Box>;

  const buildBooleanDisplay = (value: boolean, trueIcon: 'ERROR' | 'CHECK', falseIcon: 'ERROR' | 'CHECK') => {
    const trueIconComponent = trueIcon === 'ERROR'
      ? <ErrorOutlineIcon sx={{ color: 'red', fontSize: 16 }} />
      : <CheckCircleOutlineIcon sx={{ color: 'green', fontSize: 16 }} />
    const falseIconComponent = falseIcon === 'ERROR'
      ? <ErrorOutlineIcon sx={{ color: 'red', fontSize: 16 }} />
      : <CheckCircleOutlineIcon sx={{ color: 'green', fontSize: 16 }} />

    return value
      ? <Box display="flex" alignItems="center" gap={1}>{state.translate('common.yes.title')} {trueIconComponent}</Box>
      : <Box display="flex" alignItems="center" gap={1}>{state.translate('common.no.title')} {falseIconComponent}</Box>
  }

  const buildBooleanUpdate = (value: boolean, update: (checked: boolean) => FeatureTemplate) => {
    return <Switch
      checked={value}
      onChange={(_, checked) => state.onFeatureTemplateUpdated(update(checked))}
      color="primary"
    />;
  };

  const translateIconButton = (baseKey: string, type: 'module' | 'feature') => (
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); setTranslationTarget({baseKey, type}); }}
      sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
    >
      <TranslateIcon fontSize="small" />
    </IconButton>
  );

  const columns: Column<FeatureTemplate>[] = [
    createColumn({
      key: 'featureType',
      label: state.translateLocal('column.feature'),
      render: (row) => state.translate(`licenseDetails.feature.type.${row.featureType}`),
    }),
    createColumn({
      key: 'enabled', includeInParentRow: false, label: state.translateLocal('column.enabled'), render: (row) => {
        if (state.massiveAction === 'update_feature_template') {
          return buildBooleanUpdate(row.enabled, (checked) => ({...row, enabled: checked}));
        }
        return buildBooleanDisplay(row.enabled, 'CHECK', 'ERROR');
      }
    }),
    createColumn({
      key: 'activationCost', includeInParentRow: false, label: state.translateLocal('column.activationCost'), render: (row) => {
        if (state.massiveAction === 'update_feature_template') {
          return buildActivationCostInput(row);
        }
        return formatCurrency(row.activationCost);
      }
    }),
    createColumn({
      key: 'deliveryDate', includeInParentRow: false, includeInChildRow: true, label: state.translateLocal('column.deliveryDate'), render: (row) => {
        if (state.massiveAction === 'update_feature_template') {
          return buildDeliveryDateInput(row);
        }
        return formatDate(row.deliveryDate);
      }
    }),
    createColumn({
      key: 'translate_action' as any,
      label: '',
      includeInParentRow: false,
      render: (row) => <Box display="flex" justifyContent="flex-end">{translateIconButton(`licenseDetails.feature.type.${row.featureType}`, 'feature')}</Box>,
    }),
  ];

  return (
    <Paper sx={{ height: '100%', width: '100%', p: 2 }}>
      <CmsFeatureTemplateActionsBar
        massiveAction={state.massiveAction}
        setMassiveAction={state.setMassiveAction}
        isSaveDisabled={state.isSaveDisabled}
        areAllExpanded={state.areAllExpanded}
        toggleExpandAll={state.toggleExpandAll}
        translate={state.translate}
        translateLocal={state.translateLocal}
        onConfirmSave={state.updateFeatureTemplates}
      />
      <ExpandableTable
        rows={state.mainTableRows}
        columns={columns}
        getChildRows={state.getChildRows}
        isRowOpen={(row) => state.isRowOpen(row.id)}
        onRowToggle={(row) => state.handleRowToggle(row.id)}
        rowKey={(row) => row.id}
        renderParentRow={(row) => (
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <span>{state.translate(`licenseDetails.feature.module.${row.featureModule}`)}</span>
            {translateIconButton(`licenseDetails.feature.module.${row.featureModule}`, 'module')}
          </Box>
        )}
      />

      <TranslationEditDrawer
        open={!!translationTarget}
        baseKey={translationTarget?.baseKey ?? ''}
        type={translationTarget?.type ?? 'feature'}
        onClose={() => setTranslationTarget(null)}
        onSaved={refreshTranslations}
      />
    </Paper>
  );
}
